import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { StatusPill } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { asApiError } from '@/services/apiError'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { AgentGuardrailsEditor } from '@/features/agentsettings/components/AgentGuardrailsEditor'
import { useAgentSettingsLabels } from '@/features/agentsettings/agentSettingsLabels'
import {
  agentSettingsEntryInvalidated,
  connectorPolicyRefetchArmed,
  clientScopesRefetchArmed,
  openAgentSettingsClientScopes,
  openAgentSettings,
  openConnectorPolicy,
  selectAgentSettingsError,
  selectAgentSettingsLoadedFor,
  selectAgentSettingsLoading,
  selectConnectorPolicy,
  selectConnectorPolicyError,
  selectConnectorPolicyLoading,
  selectAgentSettingsClientScopes,
  selectAgentSettingsClientScopesError,
  selectAgentSettingsClientScopesLoading,
} from '@/features/agentsettings/slices/agentSettingsSlice'
import {
  AGENT_PHASES,
  type AgentPhase,
  type AgentSettingsKey,
} from '@/features/agentsettings/types'
import {
  agentsRefetchArmed,
  openAgents,
  selectAgentById,
  selectAgentsError,
  selectAgentsLoading,
} from '@/features/agents/slices/agentsSlice'
import { useAppDispatch, useAppSelector } from '@/store'

/** Live guardrails view: every policy row and action is backed by its owner API. */
export default function AgentGuardrailsPage() {
  const { t } = useTranslation('agents')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { agentId = '' } = useParams<{ agentId: string }>()
  const orgId = useAppSelector(selectActiveOrgId) ?? ''
  const agent = useAppSelector((state) => selectAgentById(state, agentId))
  const agentsLoading = useAppSelector(selectAgentsLoading)
  const agentsError = useAppSelector(selectAgentsError)
  const [phase, setPhase] = useState<AgentPhase>('discover')
  const [scope, setScope] = useState<'FIRM' | 'CLIENT'>('FIRM')
  const [clientScopeKey, setClientScopeKey] = useState('')
  const clientScopes = useAppSelector((state) =>
    selectAgentSettingsClientScopes(state, orgId)
  )
  const clientScopesLoading = useAppSelector((state) =>
    selectAgentSettingsClientScopesLoading(state, orgId)
  )
  const clientScopesError = useAppSelector((state) =>
    selectAgentSettingsClientScopesError(state, orgId)
  )
  const settingsKey = useMemo<AgentSettingsKey | null>(
    () =>
      agent === undefined || orgId === ''
        ? null
        : scope === 'FIRM'
          ? { orgId, scope, phase, agentKey: agent.id }
          : clientScopeKey === '' ? null : { orgId, scope, scopeKey: clientScopeKey, phase, agentKey: agent.id },
    [agent, clientScopeKey, orgId, phase, scope]
  )
  const labels = useAgentSettingsLabels()
  // A reread of an already-loaded entry keeps the editor mounted; only a
  // first read shows the loading panel.
  const settingsLoading = useAppSelector(
    (state) =>
      selectAgentSettingsLoading(state) &&
      (settingsKey === null ||
        !selectAgentSettingsLoadedFor(state, settingsKey))
  )
  // The list-level read error only: operation refusals live on their own
  // channel inside the form, so this retry is always reachable.
  const settingsError = useAppSelector(selectAgentSettingsError)
  const policy = useAppSelector((state) => selectConnectorPolicy(state, orgId))
  const policyLoading = useAppSelector((state) =>
    selectConnectorPolicyLoading(state, orgId)
  )
  const policyError = useAppSelector((state) =>
    selectConnectorPolicyError(state, orgId)
  )

  useEffect(() => {
    if (orgId !== '') void dispatch(openAgents({ orgId }))
  }, [dispatch, orgId])

  useEffect(() => {
    if (agent !== undefined && orgId !== '')
      void dispatch(openAgentSettingsClientScopes({ orgId }))
  }, [agent, dispatch, orgId])

  useEffect(() => {
    if (settingsKey !== null) void dispatch(openAgentSettings(settingsKey))
  }, [dispatch, settingsKey])

  useEffect(() => {
    if (agent !== undefined && orgId !== '')
      void dispatch(openConnectorPolicy({ orgId }))
  }, [agent, dispatch, orgId])

  const retryAgents = () => {
    dispatch(agentsRefetchArmed())
    void dispatch(openAgents({ orgId }))
  }
  const retrySettings = () => {
    if (settingsKey === null) return
    dispatch(agentSettingsEntryInvalidated())
    void dispatch(openAgentSettings(settingsKey))
  }
  const retryPolicy = () => {
    dispatch(connectorPolicyRefetchArmed())
    void dispatch(openConnectorPolicy({ orgId }))
  }
  const retryClientScopes = () => {
    dispatch(clientScopesRefetchArmed())
    void dispatch(openAgentSettingsClientScopes({ orgId }))
  }

  return (
    <AppShell
      moduleLabel={t('chrome.moduleLabel')}
      breadcrumb={
        <Link
          to="/library/agents"
          className="underline underline-offset-2 hover:no-underline"
        >
          {t('configuration.back')}
        </Link>
      }
      contextPill={t('guardrails.contextPill')}
      nav={libraryNav}
    >
      {agentsLoading ? (
        <LoadingPanel label={t('configuration.loadingAgent')} />
      ) : agentsError !== null ? (
        <ErrorPanel error={agentsError} onRetryClicked={retryAgents} />
      ) : agent === undefined ? (
        <EmptyPanel
          title={t('configuration.notFound')}
          body={t('configuration.notFoundBody')}
          actionLabel={t('configuration.back')}
          onActionClicked={() => navigate('/library/agents')}
        />
      ) : (
        <>
          <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
            <div>
              <h1 className="font-serif text-2xl font-semibold">{agent.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                {agent.category !== undefined && (
                  <StatusPill tone="neutral">{agent.category}</StatusPill>
                )}
                <StatusPill tone={agent.isConfigured ? 'decided' : 'neutral'}>
                  {agent.isConfigured
                    ? t('catalogue.configured')
                    : t('catalogue.notConfigured')}
                </StatusPill>
              </div>
            </div>
          </header>
          <label
            className="mt-5 block max-w-sm text-sm font-medium"
            htmlFor="agent-guardrails-phase"
          >
            {t('configuration.phase')}
            <select
              id="agent-guardrails-phase"
              value={phase}
              onChange={(event) => setPhase(event.target.value as AgentPhase)}
              className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              {AGENT_PHASES.map((item) => (
                <option key={item} value={item}>
                  {t(`phases.${item}`)}
                </option>
              ))}
            </select>
          </label>
          <div className="mt-4 grid max-w-sm gap-3">
            <label className="block text-sm font-medium" htmlFor="agent-guardrails-scope">
              {t('guardrails.override.scope')}
              <select id="agent-guardrails-scope" value={scope}
                onChange={(event) => {
                  setScope(event.target.value as 'FIRM' | 'CLIENT')
                  setClientScopeKey('')
                }}
                className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm">
                <option value="FIRM">{labels.scope('FIRM')}</option>
                <option value="CLIENT">{labels.scope('CLIENT')}</option>
              </select>
            </label>
            {scope === 'CLIENT' && (
              <label className="block text-sm font-medium" htmlFor="agent-guardrails-client">
                {t('guardrails.override.client')}
                <select id="agent-guardrails-client" value={clientScopeKey}
                  disabled={clientScopesLoading}
                  onChange={(event) => setClientScopeKey(event.target.value)}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm">
                  <option value="">{t('guardrails.override.chooseClient')}</option>
                  {clientScopes.map((client) => <option key={client.scopeKey} value={client.scopeKey}>{client.name}</option>)}
                </select>
                {clientScopesError !== null && <ErrorPanel error={asApiError({ status: clientScopesError.status, key: clientScopesError.code, message: clientScopesError.message })} onRetryClicked={retryClientScopes} />}
              </label>
            )}
          </div>
          {settingsLoading ? (
            <div className="mt-5">
              <LoadingPanel label={t('configuration.loadingSettings')} />
            </div>
          ) : settingsError !== null ? (
            <div className="mt-5">
              <ErrorPanel
                error={asApiError({
                  status: settingsError.status,
                  key: settingsError.code,
                  message: settingsError.message,
                })}
                onRetryClicked={retrySettings}
              />
            </div>
          ) : settingsKey !== null ? (
            <AgentGuardrailsEditor
              settingsKey={settingsKey}
              agent={agent}
              policy={policy}
              policyLoading={policyLoading}
              policyError={policyError}
              onPolicyRetry={retryPolicy}
            />
          ) : (
            <div className="mt-5" role="status">{t('guardrails.override.chooseClient')}</div>
          )}
        </>
      )}
    </AppShell>
  )
}
